import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaPrint,
  FaSearch,
  FaTimes,
  FaDownload,
  FaBoxOpen,
} from "react-icons/fa";

import {
  PackageOpen,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const StoreIssueReport = () => {
  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showForm,
    setShowForm,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const branchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const branchType =
    (
      localStorage.getItem(
        "branchType"
      ) || "HO"
    ).toUpperCase();

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  const loginUserId =
    localStorage.getItem(
      "loginUserId"
    ) ||
    userId ||
    "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          branchId,

        "x-branch":
          branchType,

        "x-userid":
          userId,

        "x-loginuserid":
          loginUserId,
      }),
      [
        finYearId,
        finYear,
        branchId,
        branchType,
        userId,
        loginUserId,
      ]
    );

  /* =========================================================
     FORM STATE
  ========================================================= */

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    issueNo,
    setIssueNo,
  ] = useState("");

  const [
    store,
    setStore,
  ] = useState("");

  const [
    itemCode,
    setItemCode,
  ] = useState("");

  const [
    itemName,
    setItemName,
  ] = useState("");

  /* =========================================================
     DATA STATE
  ========================================================= */

  const [
    storeList,
    setStoreList,
  ] = useState([]);

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     DESIGN CLASSES
     SAME STYLE AS DEPARTMENT MASTER
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     FETCH STORE DROPDOWN
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        `https://nissi-india.com/api/getStoresByBranchId_siRpt?branchID=${branchId}`,
        {
          headers:
            commonHeaders,
        }
      )
      .then(
        (res) => {
          const stores =
            res.data
              ?.stores ||
            [];

          setStoreList(
            stores
          );
        }
      )
      .catch(
        (err) => {
          console.error(
            "Dropdown fetch failed",
            err
          );

          toast.error(
            "Failed to load stores"
          );
        }
      )
      .finally(() => {
        setLoading(false);
      });
  }, [
    branchId,
    commonHeaders,
  ]);

  /* =========================================================
     FORMAT DATE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const formatDate =
    (dateStr) => {
      if (!dateStr) {
        return "";
      }

      const [
        year,
        month,
        day,
      ] =
        dateStr.split("-");

      return `${day}/${month}/${year}`;
    };

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch =
    () => {
      if (
        !fromDate ||
        !toDate
      ) {
        toast.warning(
          "Please select both From Date and To Date"
        );

        return;
      }

      setLoading(true);

      const params = {
        branchID:
          branchId,

        storeIssueNo:
          issueNo ||
          "",

        fromDate:
          formatDate(
            fromDate
          ),

        toDate:
          formatDate(
            toDate
          ),

        store:
          store ||
          "",

        itemname:
          itemName ||
          "",

        itemcode:
          itemCode ||
          "",
      };

      axios
        .get(
          "https://nissi-india.com/api/storeIssueReport",
          {
            params,

            headers:
              commonHeaders,
          }
        )
        .then(
          (res) => {
            const list =
              Array.isArray(
                res.data
              )
                ? res.data
                : Array.isArray(
                    res.data
                      ?.data
                  )
                ? res.data
                    .data
                : [];

            setData(
              list
            );

            toast.success(
              `Loaded ${list.length} records`
            );
          }
        )
        .catch(
          (err) => {
            console.error(
              "Store Issue fetch failed",
              err
            );

            toast.error(
              "Failed to fetch store issue listing"
            );
          }
        )
        .finally(() => {
          setLoading(
            false
          );
        });
    };

  /* =========================================================
     PRINT ALL
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint =
    () => {
      const params =
        new URLSearchParams(
          {
            branchID:
              branchId,

            storeIssueNo:
              issueNo ||
              "",

            fromDate:
              formatDate(
                fromDate
              ),

            toDate:
              formatDate(
                toDate
              ),

            store:
              store ||
              "",

            itemname:
              itemName ||
              "",

            itemcode:
              itemCode ||
              "",
          }
        ).toString();

      window.open(
        `/inventory/StoreIssueReport_AllPrint?${params}`,
        "_blank"
      );
    };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters =
    () => {
      setFromDate("");

      setToDate("");

      setIssueNo("");

      setStore("");

      setItemCode("");

      setItemName("");

      setData([]);
    };

  /* =========================================================
     CURRENCY
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const formatCurrency =
    (amount) => {
      return new Intl.NumberFormat(
        "en-IN",
        {
          style:
            "currency",

          currency:
            "INR",

          minimumFractionDigits:
            2,

          maximumFractionDigits:
            2,
        }
      ).format(
        amount ||
          0
      );
    };

  /* =========================================================
     EXPORT
     IMPORTANT:
     ORIGINAL PAGE ONLY HAD A VISUAL EXPORT BUTTON.
     NO EXPORT HANDLER EXISTED.
     DO NOT ADD NEW FUNCTIONALITY HERE.
  ========================================================= */

  const handleExport =
    () => {
      toast.info(
        "Export functionality is not implemented in the existing Store Issue Report."
      );
    };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Store Issue Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              FORM HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <PackageOpen className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Store Issue Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Track and manage all store issue transactions
                </p>
              </div>
            </div>

            {/* =============================================
                HEADER ACTIONS
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT
                  SAME THEME COLOR AS SEARCH */}

              <button
                type="button"
                onClick={
                  handleExport
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload
                  size={
                    14
                  }
                />

                Export
              </button>

              {/* SHOW / HIDE */}

              <button
                type="button"
                onClick={() =>
                  setShowForm(
                    !showForm
                  )
                }
                className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                {showForm
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FORM BODY
          =============================================== */}

          {showForm && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div
                className="
                  grid
                  grid-cols-1
                  md:grid-cols-2
                  lg:grid-cols-3
                  gap-5
                "
              >

                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(
                      e
                    ) =>
                      setFromDate(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                    required
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(
                      e
                    ) =>
                      setToDate(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                    required
                  />
                </div>

                {/* ISSUE NO */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Issue No
                  </label>

                  <input
                    type="text"
                    value={
                      issueNo
                    }
                    onChange={(
                      e
                    ) =>
                      setIssueNo(
                        e.target
                          .value
                      )
                    }
                    placeholder="Enter issue number"
                    className={
                      inputClass
                    }
                    onKeyDown={(
                      e
                    ) => {
                      if (
                        e.key ===
                        "Enter"
                      ) {
                        handleSearch();
                      }
                    }}
                  />
                </div>

                {/* STORE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Store
                  </label>

                  <select
                    value={
                      store
                    }
                    onChange={(
                      e
                    ) =>
                      setStore(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Stores
                    </option>

                    {storeList.map(
                      (
                        s
                      ) => (
                        <option
                          key={
                            s.fld_storeid
                          }
                          value={
                            s.fld_storeid
                          }
                        >
                          {
                            s.fld_storename
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* ITEM CODE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Code
                  </label>

                  <input
                    type="text"
                    value={
                      itemCode
                    }
                    onChange={(
                      e
                    ) =>
                      setItemCode(
                        e.target
                          .value
                      )
                    }
                    placeholder="Enter item code"
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* ITEM NAME */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Name
                  </label>

                  <input
                    type="text"
                    value={
                      itemName
                    }
                    onChange={(
                      e
                    ) =>
                      setItemName(
                        e.target
                          .value
                      )
                    }
                    placeholder="Enter item name"
                    className={
                      inputClass
                    }
                  />
                </div>
              </div>

              {/* =============================================
                  FORM ACTIONS
              ============================================= */}

              <div
                className="
                  flex
                  flex-wrap
                  justify-end
                  gap-3
                  mt-6
                  pt-5
                  border-t
                  border-slate-200
                  dark:border-slate-700
                "
              >

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={
                      14
                    }
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2.5
                    text-sm
                    font-semibold
                    rounded-xl
                    transition
                    ${
                      isDark
                        ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }
                  `}
                >
                  <FaPrint
                    size={
                      14
                    }
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span
                      className="
                        w-4
                        h-4
                        border-2
                        border-white
                        border-t-transparent
                        rounded-full
                        animate-spin
                      "
                    />
                  ) : (
                    <FaSearch
                      size={
                        14
                      }
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div
            className="
              p-6
              border-b
              border-gray-200
              dark:border-gray-700
            "
          >
            <div
              className="
                flex
                flex-col
                sm:flex-row
                items-start
                sm:items-center
                justify-between
                gap-4
              "
            >

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Store Issue Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {
                    data.length
                  }{" "}
                  records
                </span>
              </div>

              {/* LAST UPDATED - EXISTING INFORMATION PRESERVED */}

              {data.length >
                0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div
              className="
                flex
                flex-col
                items-center
                justify-center
                py-16
              "
            >
              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading store issue records...
              </p>
            </div>
          ) : data.length >
            0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Issue No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Total Value
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Username
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Print
                    </th>
                  </tr>
                </thead>

                {/* BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={
                          item.fld_storesissueid ||
                          index
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* SERIAL NO */}

                        <td className="px-4 py-3 font-medium">
                          {
                            index +
                            1
                          }
                        </td>

                        {/* ISSUE NO */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_storesissueno ||
                              "-"}
                          </span>
                        </td>

                        {/* DATE */}

                        <td className="px-4 py-3 whitespace-nowrap">

                          {item.fld_date ||
                            "-"}
                        </td>

                        {/* TOTAL */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          {formatCurrency(
                            item.totalNew
                          )}
                        </td>

                        {/* USERNAME */}

                        <td className="px-4 py-3 font-medium">

                          {item.fld_username ||
                            "-"}
                        </td>

                        {/* PRINT */}

                        <td className="px-4 py-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              window.open(
                                `/inventory/StoreIssueReport_Print?id=${item.fld_storesissueid}`,
                                "_blank"
                              )
                            }
                            className={`p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                                : "bg-green-100 text-green-600 hover:bg-green-200"
                            }`}
                            title="Print Report"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div
              className="
                px-4
                py-14
                text-center
              "
            >
              <FaBoxOpen
                size={
                  40
                }
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No store issue records
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={
          3000
        }
        hideProgressBar={
          false
        }
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default StoreIssueReport;